/**
 * INSTALL-SPEC-02 — the install details, tailored to the equipment (Seth
 * 2026-09-25: "Equipment should be the first to be selected. Then the install
 * details should be tailored around that. If we are installing an 80% then PVC
 * termination shouldn't be a thing. If no AC then nothing with AC stuff …
 * PVC termination should have an option for replacing existing PVC and how
 * much. Is it 1 piped or 2 … research what typical install details would be").
 *
 * Built from Sedgwick's own paper Installation Work Order (the job packet's
 * "Work Order" tab: FURNACE INSTALLATION · AIR COND. · ACCESSORIES · WATER
 * HEATER · Ductwork · Asbestos · Key Arrangements · "A/C must be wired to
 * charge unit properly") plus the trade's standard pre-install questions
 * (1-pipe vs 2-pipe PVC, the orphaned water heater left alone in the chimney,
 * line set reuse vs flush vs new, heat-pump stands above the snow line, backup
 * heat, crane for a rooftop).
 *
 * How it works: the equipment on the job becomes TAGS (furnace, furnace80 /
 * furnace90, cooling, heatpump, coil, airhandler, minisplit, boiler,
 * waterheater, unitheater, packaged) plus every equipment key on it as-is
 * (pool-heater, humidifier, thermostat … — so a category added in Settings can
 * drive questions too); each question says which tags it needs, so an 80%
 * furnace never asks PVC and a furnace-only job never asks line set.
 * A furnace's efficiency comes from the pricebook item ("97% AFUE") or its
 * model number ("SLP98…", "ML180…"); the salesperson can always flip it.
 * Items on the job pre-answer questions too — a line set on the job means New
 * line set, a thermostat means New thermostat, a chimney liner means a liner.
 *
 * Answers live on the job (install_spec jsonb): `<key>` = the chip,
 * `<key>_amt` = how much, `<key>_note` = the detail. INSTALL-SPEC-01 answers
 * (pvc, lineset, cond_pad …) keep their keys and show as-is, so nothing typed
 * is lost. Shared by the office Work Order, the printed / emailed work order
 * and the crew's phone — the three can never drift.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useEquipmentCategories } from "@/features/settings/equipmentCategories";

export type EquipTag =
  | "furnace" | "furnace80" | "furnace90" | "boiler"
  | "cooling" | "heatpump" | "coil" | "airhandler" | "minisplit"
  | "waterheater" | "unitheater" | "packaged";

/** A section key — the built-in ones below, or any the office adds in Settings. */
export type SectionKey = string;

export const SECTIONS: { key: SectionKey; title: string }[] = [
  { key: "furnace", title: "Furnace — Efficiency & Venting" },
  { key: "gas", title: "Gas Line" },
  { key: "indoor", title: "Indoor Unit — Plenums, Filter & Power" },
  { key: "cooling", title: "Air Conditioner / Heat Pump" },
  { key: "coil", title: "Coil & Drain" },
  { key: "minisplit", title: "Mini-Split" },
  { key: "packaged", title: "Packaged Unit" },
  { key: "boiler", title: "Boiler" },
  { key: "waterheater", title: "Water Heater" },
  { key: "unitheater", title: "Garage / Unit Heater" },
  { key: "accessories", title: "Accessories — Reconnect or New" },
  { key: "ducts", title: "Ductwork" },
  { key: "site", title: "Site & Access" },
];

export interface SpecQuestion {
  key: string;
  section: SectionKey;
  label: string;
  /** Chips — one pick. */
  options?: string[];
  /** Picks that ask "how much?" (the amount box shows beside them). */
  amountFor?: string[];
  /** The amount's unit — ft, kW. */
  unit?: string;
  /** Placeholder for the detail box; omit = no detail box. */
  note?: string;
  /** A one-line hint under the label (why the question matters). */
  hint?: string;
  /** Show the hint only when the job has one of these tags (or equipment keys). */
  hintWhen?: string[];
  /** Free text only (no chips). */
  text?: boolean;
  /** Shows when the job has ANY of these tags or equipment keys (omit = always). */
  when?: string[];
  /** …but never when it has any of these. */
  unless?: string[];
  /** Shows only when another answer is one of these. */
  whenAnswer?: { key: string; in: string[] };
  /** Pre-answered by what's on the job: an equipment key or a tag → the pick. */
  implied?: { equip?: string; tag?: string; pick: string }[];
  /** Not counted toward "done" (nice to have). */
  optional?: boolean;
  /** Drives other questions (the furnace's efficiency) — its choices can't be changed in Settings. */
  locked?: boolean;
}

/** INSTALL-SPEC-ADMIN: the question bank — built in below, editable in Settings → Install Details. */
export interface InstallBank { sections: { key: SectionKey; title: string }[]; questions: SpecQuestion[] }

const FORCED_AIR: EquipTag[] = ["furnace", "airhandler", "coil", "cooling"];

export const SPEC_QUESTIONS: SpecQuestion[] = [
  // ── Furnace ──────────────────────────────────────────────────────────────
  { key: "furnace_eff", section: "furnace", label: "Furnace Efficiency", options: ["80% · Metal Vent", "90%+ · PVC"], when: ["furnace"], hint: "Decides the venting questions below", locked: true },
  // 90%+ (condensing, PVC)
  { key: "vent_pipes", section: "furnace", label: "PVC Venting", options: ["2-Pipe (Sealed)", "1-Pipe (Indoor Air)"], when: ["furnace90"], hint: "2-pipe brings combustion air in from outside" },
  { key: "vent_size", section: "furnace", label: "Pipe Size", options: ['2"', '3"', "Per Manual"], when: ["furnace90"] },
  { key: "pvc", section: "furnace", label: "PVC Pipe", options: ["Reuse Existing", "Replace Existing", "New (80→90)"], amountFor: ["Replace Existing", "New (80→90)"], unit: "ft", note: "route, elbows…", when: ["furnace90"] },
  { key: "vent_term", section: "furnace", label: "Termination", options: ["Reuse Existing", "Sidewall", "Concentric Kit", "Roof", "Low-Profile Kit"], note: "where on the house…", when: ["furnace90"] },
  { key: "condensate", section: "furnace", label: "Condensate", options: ["Floor Drain", "Reuse Pump", "New Pump"], note: "neutralizer, route…", when: ["furnace90"], implied: [{ equip: "condensate-pump", pick: "New Pump" }], hint: "Furnace and coil drain together" },
  { key: "orphan_wh", section: "furnace", label: "Orphaned Water Heater", options: ["Not Applicable", "Liner Needed", "Existing Liner OK"], amountFor: ["Liner Needed"], unit: "ft", when: ["furnace90"], whenAnswer: { key: "pvc", in: ["New (80→90)"] }, implied: [{ equip: "chimney-liner", pick: "Liner Needed" }], hint: "With the furnace off the chimney, the water heater vents alone" },
  // 80% (atmospheric, metal vent)
  { key: "vent_connector", section: "furnace", label: "Vent Connector", options: ["Reuse", "New"], amountFor: ["New"], unit: "ft", when: ["furnace80"] },
  { key: "chimney", section: "furnace", label: "Chimney / Liner", options: ["Existing OK", "New Liner", "B-Vent"], amountFor: ["New Liner", "B-Vent"], unit: "ft", when: ["furnace80"], implied: [{ equip: "chimney-liner", pick: "New Liner" }] },
  { key: "flue", section: "furnace", label: "Flue Modifications", options: ["None", "As Needed"], note: "detail…", when: ["furnace80"] },
  { key: "combustion_air", section: "furnace", label: "Combustion Air", options: ["Existing OK", "Add Intake"], note: "detail…", when: ["furnace80"] },
  // ── Gas (anything that burns it) ─────────────────────────────────────────
  { key: "gas_line", section: "gas", label: "Gas Line", options: ["Reuse", "Modify", "New Run"], amountFor: ["New Run"], unit: "ft", note: "shutoff, drip leg…", when: ["furnace", "boiler", "unitheater", "packaged"] },
  // ── Indoor unit (furnace or air handler) ─────────────────────────────────
  { key: "return_air", section: "indoor", label: "Return-Air Drop", options: ["Reuse", "Modify", "New"], note: "detail…", when: ["furnace", "airhandler"] },
  { key: "supply_plenum", section: "indoor", label: "Supply Plenum", options: ["Reuse", "New", "Transition"], when: ["furnace", "airhandler"] },
  { key: "filter", section: "indoor", label: "Filter", options: ['1" Rack', "Media Cabinet", "Air Cleaner"], when: ["furnace", "airhandler"] },
  { key: "furnace_power", section: "indoor", label: "Indoor Unit Power", options: ["Reuse Circuit", "New Circuit"], when: ["furnace", "airhandler"] },
  // ── Air conditioner / heat pump (split systems) ───────────────────────────
  { key: "lineset", section: "cooling", label: "Line Set", options: ["Reuse", "Flush & Reuse", "New"], amountFor: ["New"], unit: "ft", note: "sizes, routing…", when: ["cooling"], implied: [{ equip: "lineset", pick: "New" }] },
  { key: "cond_location", section: "cooling", label: "Outdoor Unit Location", options: ["Same Spot", "New Spot"], note: "where…", when: ["cooling"] },
  { key: "cond_pad", section: "cooling", label: "Pad / Mount", options: ["Reuse Pad", "New Pad", "Stand / Riser", "Wall Bracket"], when: ["cooling"], implied: [{ equip: "pad", pick: "New Pad" }], hint: "Heat pumps usually go up on a stand, above the snow", hintWhen: ["heatpump"] },
  { key: "cooling_power", section: "cooling", label: "Electrical (Sub)", options: ["Reuse Disconnect", "New Disconnect", "New Circuit"], note: "breaker, whip…", when: ["cooling"] },
  { key: "wired_to_charge", section: "cooling", label: "Wired to Charge on Install Day", options: ["Yes", "No — Electrician After"], when: ["cooling"], hint: "The crew can't charge the system without power" },
  { key: "backup_heat", section: "cooling", label: "Backup Heat", options: ["Dual Fuel (Furnace)", "Electric Strips", "None"], amountFor: ["Electric Strips"], unit: "kW", when: ["heatpump"], implied: [{ tag: "furnace", pick: "Dual Fuel (Furnace)" }] },
  // ── Coil / air handler ───────────────────────────────────────────────────
  { key: "coil_type", section: "coil", label: "Evaporator Coil", options: ["Cased", "Uncased"], when: ["coil", "cooling"], unless: ["airhandler"] },
  { key: "coil_transition", section: "coil", label: "Coil Transition", options: ["None", "Needed"], when: ["coil", "cooling"], unless: ["airhandler"] },
  { key: "coil_drain", section: "coil", label: "Coil Drain", options: ["Floor Drain", "Reuse Pump", "New Pump"], when: ["coil", "cooling", "airhandler"], unless: ["furnace90"], implied: [{ equip: "condensate-pump", pick: "New Pump" }] },
  // ── Mini-split ───────────────────────────────────────────────────────────
  { key: "ms_heads", section: "minisplit", label: "Indoor Heads", options: ["1", "2", "3", "4+"], note: "which rooms…", when: ["minisplit"] },
  { key: "ms_lineset", section: "minisplit", label: "Line Sets", options: ["New"], amountFor: ["New"], unit: "ft total", note: "routing, line hide…", when: ["minisplit"] },
  { key: "ms_mount", section: "minisplit", label: "Outdoor Unit Mount", options: ["Pad", "Stand", "Wall Bracket"], when: ["minisplit"] },
  { key: "ms_walls", section: "minisplit", label: "Wall Penetrations", options: ["New", "Existing"], when: ["minisplit"] },
  { key: "ms_drain", section: "minisplit", label: "Condensate", options: ["Gravity", "Pump"], when: ["minisplit"] },
  { key: "ms_power", section: "minisplit", label: "Electrical (Sub)", options: ["New Circuit", "Reuse"], note: "breaker, disconnect…", when: ["minisplit"] },
  // ── Packaged (Magic-Pak / rooftop) ───────────────────────────────────────
  { key: "pk_mount", section: "packaged", label: "Sleeve / Curb", options: ["Reuse", "New Sleeve", "Curb Adapter"], when: ["packaged"] },
  { key: "pk_crane", section: "packaged", label: "Crane", options: ["Not Needed", "Crane Needed"], note: "company, date…", when: ["packaged"] },
  { key: "pk_power", section: "packaged", label: "Electrical (Sub)", options: ["Reuse", "New Circuit"], when: ["packaged"] },
  // ── Boiler ───────────────────────────────────────────────────────────────
  { key: "boiler_vent", section: "boiler", label: "Venting", options: ["Reuse", "New PVC", "New Stainless", "Liner"], amountFor: ["New PVC", "New Stainless", "Liner"], unit: "ft", when: ["boiler"] },
  { key: "boiler_piping", section: "boiler", label: "Near-Boiler Piping", options: ["Reuse", "Repipe"], note: "detail…", when: ["boiler"] },
  { key: "boiler_zones", section: "boiler", label: "Zones", options: ["1", "2", "3", "4+"], when: ["boiler"] },
  // ── Water heater ─────────────────────────────────────────────────────────
  { key: "wh_vent", section: "waterheater", label: "Venting", options: ["Reuse", "Power Vent PVC", "Liner"], amountFor: ["Power Vent PVC", "Liner"], unit: "ft", when: ["waterheater"] },
  { key: "wh_expansion", section: "waterheater", label: "Expansion Tank", options: ["Reuse", "New", "None"], when: ["waterheater"] },
  // ── Garage / unit heater ─────────────────────────────────────────────────
  { key: "uh_mount", section: "unitheater", label: "Mounting", options: ["Reuse", "Ceiling Kit", "Wall Bracket"], when: ["unitheater"] },
  { key: "uh_vent", section: "unitheater", label: "Venting", options: ["Reuse", "Sidewall", "Roof"], amountFor: ["Sidewall", "Roof"], unit: "ft", when: ["unitheater"] },
  { key: "uh_power", section: "unitheater", label: "Electrical", options: ["Reuse", "New Circuit", "Plug-In"], when: ["unitheater"] },
  // ── Accessories (paper: Thermostat · Air Cleaner · Humidifier · Condensate Pump · HRV · Germicidal Light) ──
  { key: "acc_tstat", section: "accessories", label: "Thermostat", options: ["Reconnect", "New", "None"], note: "wiring, C-wire…", when: ["furnace", "boiler", "cooling", "airhandler", "unitheater", "packaged"], implied: [{ equip: "thermostat", pick: "New" }] },
  { key: "acc_air_cleaner", section: "accessories", label: "Air Cleaner", options: ["Reconnect", "New", "None"], when: FORCED_AIR, implied: [{ equip: "air-cleaner", pick: "New" }] },
  { key: "acc_humidifier", section: "accessories", label: "Humidifier", options: ["Reconnect", "New", "None"], note: "water feed, drain…", when: FORCED_AIR, implied: [{ equip: "humidifier", pick: "New" }] },
  { key: "acc_hrv", section: "accessories", label: "HRV / ERV", options: ["Reconnect", "New", "None"], when: ["furnace", "airhandler"], implied: [{ equip: "erv-hrv", pick: "New" }] },
  { key: "acc_uv", section: "accessories", label: "Germicidal Light", options: ["Reconnect", "New", "None"], when: FORCED_AIR, implied: [{ equip: "uv", pick: "New" }] },
  { key: "acc_zones", section: "accessories", label: "Zone System", options: ["Reconnect", "New", "None"], when: FORCED_AIR, implied: [{ equip: "zoning", pick: "New" }], optional: true },
  // ── Ductwork ─────────────────────────────────────────────────────────────
  { key: "ductwork", section: "ducts", label: "Ductwork", options: ["No Changes", "Modify", "New Runs"], note: "supplies, returns, by floor…", when: FORCED_AIR },
  // ── Site & access ────────────────────────────────────────────────────────
  { key: "asbestos", section: "site", label: "Asbestos", options: ["None", "Suspected", "Removal Scheduled"], note: "company, date…" },
  { key: "access", section: "site", label: "Equipment Is In", options: ["Basement", "Utility Room", "Attic", "Crawlspace", "Garage"], note: "tight spots, stairs…", optional: true },
  { key: "haul_away", section: "site", label: "Old Equipment", options: ["Haul Away", "Customer Keeps"] },
  { key: "key_arrangements", section: "site", label: "Key Arrangements", text: true, note: "lockbox code, garage keypad, door to use, pets…" },
  { key: "comments", section: "site", label: "Comments for the Crew", text: true, note: "anything else the crew needs to know — prints on the work order…", optional: true },
];

/** The bank as shipped — the fallback when Settings has none (or can't be read). */
export const BUILT_IN_BANK: InstallBank = { sections: SECTIONS, questions: SPEC_QUESTIONS };

/** Plain names for the equipment tags (Settings → Install Details "Ask when the job has…"). */
export const TAG_LABEL: Record<EquipTag, string> = {
  furnace: "Any Furnace", furnace80: "80% Furnace", furnace90: "90%+ Furnace", boiler: "Boiler",
  cooling: "AC or Heat Pump", heatpump: "Heat Pump", coil: "Coil", airhandler: "Air Handler",
  minisplit: "Mini-Split", waterheater: "Water Heater", unitheater: "Unit Heater", packaged: "Packaged Unit",
};
export const ALL_TAGS = Object.keys(TAG_LABEL) as EquipTag[];
/** Items on a job that can pre-answer a question. */
export const ITEM_KEY_LABEL: Record<string, string> = {
  thermostat: "Thermostat", humidifier: "Humidifier", "air-cleaner": "Air Cleaner", "erv-hrv": "HRV / ERV",
  uv: "Germicidal Light", zoning: "Zoning", lineset: "Line Set", pad: "Condenser Pad", "chimney-liner": "Chimney Liner",
  "condensate-pump": "Condensate Pump", dehumidifier: "Dehumidifier",
};

/** INSTALL-SPEC-01's free-text reconnect line, still shown if a job has it. */
const LEGACY_TEXT: { key: string; label: string }[] = [{ key: "reconnects", label: "Reconnects" }];

// ── The equipment → tags ────────────────────────────────────────────────────
export interface EquipLike {
  category: string | null;
  model: string | null;
  equip_key?: string | null;
  kind?: string | null;
  item_id?: string | null;
  /** The pricebook item's efficiency text ("97% AFUE"), when linked. */
  efficiency?: string | null;
}

/** 80 or 90 from "97% AFUE" or the model ("SLP98UH…" = 98, "ML180UH…" = 80). */
export function furnaceEffOf(e: EquipLike): { eff: "80" | "90"; from: "pricebook" | "model" } | null {
  const pct = e.efficiency?.match(/(\d{2})\s*%/);
  if (pct) return { eff: Number(pct[1]) >= 90 ? "90" : "80", from: "pricebook" };
  const m = (e.model ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (/^(?:[A-Z]{1,4}[12]?)?9[2-9](?=[A-Z])/.test(m)) return { eff: "90", from: "model" };
  if (/^(?:[A-Z]{1,4}[12]?)?80(?=[A-Z])/.test(m)) return { eff: "80", from: "model" };
  return null;
}

/** An equipment category added in Settings → Equipment Categories ("Pool Heater"). */
export interface CustomCat { key: string; label: string }

const normCat = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim().replace(/s$/, "");

/** A line's category text ("Pool Heater", "pool heaters") → the added category's key. The
 *  database classifier only knows the built-in kinds, so a custom one is matched by name. */
function customKeyOf(category: string | null, customs: CustomCat[] | undefined): string | null {
  const t = normCat(category ?? "");
  if (!t || !customs?.length) return null;
  return customs.find((c) => normCat(c.key) === t || normCat(c.label) === t)?.key ?? null;
}

/** The job's equipment key for one line — the classifier, plus the install
 *  items it doesn't name (pads, line sets, liners, zoning, condensate pumps),
 *  plus categories added in Settings (INSTALL-SPEC-ADMIN). */
export function keyOf(e: EquipLike, customs?: CustomCat[]): string | null {
  const c = (e.category ?? "").toLowerCase();
  if (/condensate pump|condo pump/.test(c)) return "condensate-pump";
  if (/\bpad\b/.test(c)) return "pad";
  if (/line ?set/.test(c)) return "lineset";
  if (/chimney|liner/.test(c)) return "chimney-liner";
  if (/\bzon(e|ing)\b/.test(c)) return "zoning";
  return e.equip_key ?? customKeyOf(e.category, customs);
}

export interface EquipFacts {
  tags: Set<EquipTag>;
  /** Equipment keys on the job (thermostat, humidifier, lineset …) — they pre-answer questions. */
  keys: Set<string>;
  /** What the equipment says about the furnace, before the salesperson's pick. */
  furnaceEffGuess: "80" | "90" | null;
  furnaceEffFrom: "pricebook" | "model" | null;
  /** How many lines are on the job (0 = nothing to tailor to yet). */
  lines: number;
  /** Categories added in Settings that are on the job (key → label), for "Tailored to". */
  customLabels?: Record<string, string>;
}

export function equipFacts(units: EquipLike[], customs?: CustomCat[]): EquipFacts {
  const keys = new Set<string>();
  const tags = new Set<EquipTag>();
  const customLabels: Record<string, string> = {};
  let eff: { eff: "80" | "90"; from: "pricebook" | "model" } | null = null;
  for (const u of units) {
    const k = keyOf(u, customs);
    if (!k) continue;
    keys.add(k);
    const cc = customs?.find((c) => c.key === k);
    if (cc) customLabels[k] = cc.label;
    if (k === "furnace") { tags.add("furnace"); eff = eff ?? furnaceEffOf(u); }
    if (k === "boiler") tags.add("boiler");
    if (k === "ac" || k === "heat-pump") tags.add("cooling");
    if (k === "heat-pump") tags.add("heatpump");
    if (k === "coil") tags.add("coil");
    if (k === "air-handler") tags.add("airhandler");
    if (k === "mini-split") tags.add("minisplit");
    if (k === "water-heater") tags.add("waterheater");
    if (k === "garage-heater") tags.add("unitheater");
    if (k === "magic-pak" || k === "rooftop") tags.add("packaged");
  }
  return { tags, keys, furnaceEffGuess: eff?.eff ?? null, furnaceEffFrom: eff?.from ?? null, lines: units.length, customLabels };
}

/** The tags after the salesperson's furnace pick (the chip wins over the guess),
 *  plus every equipment key on the job (INSTALL-SPEC-ADMIN: "pool-heater",
 *  "humidifier" … can drive a question added in Settings). */
export function effectiveTags(f: EquipFacts, spec: Record<string, string> | null | undefined): Set<string> {
  const t = new Set<string>([...f.tags, ...f.keys]);
  if (t.has("furnace")) {
    const pick = spec?.furnace_eff ?? "";
    const eff = pick.startsWith("80") ? "80" : pick.startsWith("90") ? "90" : f.furnaceEffGuess;
    if (eff === "80") t.add("furnace80");
    if (eff === "90") t.add("furnace90");
  }
  return t;
}

export function applies(q: SpecQuestion, tags: Set<string>, spec: Record<string, string> | null | undefined): boolean {
  if (q.when && !q.when.some((w) => tags.has(w))) return false;
  if (q.unless && q.unless.some((w) => tags.has(w))) return false;
  if (q.whenAnswer && !q.whenAnswer.in.includes((spec?.[q.whenAnswer.key] ?? "").trim())) return false;
  return true;
}

/** What the equipment answers for a question on its own (before anyone taps). */
export function impliedPick(q: SpecQuestion, f: EquipFacts | null): string {
  if (!f) return "";
  if (q.key === "furnace_eff" && f.furnaceEffGuess) {
    const want = f.furnaceEffGuess === "80" ? "80" : "90";
    return (q.options ?? []).find((o) => o.trim().startsWith(want)) ?? "";
  }
  for (const i of q.implied ?? []) {
    if (i.equip && f.keys.has(i.equip)) return i.pick;
    if (i.tag && ((f.tags as Set<string>).has(i.tag) || f.keys.has(i.tag))) return i.pick;
  }
  return "";
}

/** The pick for a question — what's saved, else what the equipment implies. */
export function pickOf(q: SpecQuestion, spec: Record<string, string> | null | undefined, f: EquipFacts | null): string {
  const v = (spec?.[q.key] ?? "").trim();
  return v || impliedPick(q, f);
}

export function isAnswered(q: SpecQuestion, spec: Record<string, string> | null | undefined, f: EquipFacts | null): boolean {
  return pickOf(q, spec, f) !== "" || (spec?.[`${q.key}_note`] ?? "").trim() !== "";
}

/** Applicable questions, answered count (optional ones don't count toward done). */
export function specProgress(spec: Record<string, string> | null | undefined, f: EquipFacts | null, bank: InstallBank = BUILT_IN_BANK) {
  const tags = f ? effectiveTags(f, spec) : new Set<string>();
  const secs = new Set(bank.sections.map((x) => x.key));
  const qs = bank.questions.filter((q) => secs.has(q.section) && applies(q, tags, spec));
  const counted = qs.filter((q) => !q.optional);
  const answered = counted.filter((q) => isAnswered(q, spec, f)).length;
  // Answers typed earlier that no longer fit (the furnace flipped to 80%, the AC came off the job).
  const stranded = bank.questions.filter((q) => !applies(q, tags, spec)
    && ((spec?.[q.key] ?? "").trim() !== "" || (spec?.[`${q.key}_note`] ?? "").trim() !== "" || (spec?.[`${q.key}_amt`] ?? "").trim() !== ""));
  return {
    questions: qs, tags, answered, total: counted.length, stranded,
    complete: counted.length > 0 && answered === counted.length,
  };
}

/** Answered count over the applicable list. */
export function specFilledCount(spec: Record<string, string> | null | undefined, f: EquipFacts | null = null, bank: InstallBank = BUILT_IN_BANK): number {
  return specProgress(spec, f, bank).answered;
}

export interface SpecLine { key: string; section: SectionKey | "legacy"; label: string; value: string }

/** The answered lines as label → "Pick · 20 ft — detail", in section order.
 *  With the equipment facts, only questions that fit the job print (and the
 *  equipment's own answers — a New thermostat — print too). */
export function specLines(spec: Record<string, string> | null | undefined, f: EquipFacts | null = null, bank: InstallBank = BUILT_IN_BANK): SpecLine[] {
  const s0 = spec ?? {};
  const tags = f ? effectiveTags(f, s0) : null;
  const out: SpecLine[] = [];
  for (const s of bank.sections) {
    for (const q of bank.questions.filter((x) => x.section === s.key)) {
      if (tags && !applies(q, tags, s0)) continue;
      const pick = pickOf(q, s0, f);
      const amt = (s0[`${q.key}_amt`] ?? "").trim();
      const note = (s0[`${q.key}_note`] ?? "").trim();
      // The amount prints with a "how much" pick — or with a pick that's no longer
      // one of the choices (reworded or removed in Settings after it was picked).
      const amtShows = !!pick && !!amt && (!!q.amountFor?.includes(pick) || !(q.options ?? []).includes(pick));
      const head = [pick, amtShows ? `${amt} ${q.unit ?? ""}`.trim() : ""].filter(Boolean).join(" · ");
      const value = [head, note].filter(Boolean).join(" — ");
      if (value) out.push({ key: q.key, section: s.key, label: q.label, value });
    }
  }
  for (const l of LEGACY_TEXT) {
    const v = (s0[l.key] ?? "").trim();
    if (v) out.push({ key: l.key, section: "legacy", label: l.label, value: v });
  }
  return out;
}

/** Lines grouped under their section titles (for the printed work order and the phone). */
export function specGroups(lines: SpecLine[], bank: InstallBank = BUILT_IN_BANK): { title: string; lines: SpecLine[] }[] {
  const out: { title: string; lines: SpecLine[] }[] = [];
  for (const s of bank.sections) {
    const ls = lines.filter((l) => l.section === s.key);
    if (ls.length) out.push({ title: s.title, lines: ls });
  }
  const legacy = lines.filter((l) => l.section === "legacy");
  if (legacy.length) out.push({ title: "Other", lines: legacy });
  return out;
}

/** Plain words for what the questions were tailored to ("97% Furnace", "Air Conditioner", "Thermostat"). */
export function tailoredTo(f: EquipFacts, spec: Record<string, string> | null | undefined): string[] {
  const tags = effectiveTags(f, spec);
  const out: string[] = [];
  if (tags.has("furnace")) out.push(tags.has("furnace90") ? "90%+ Furnace" : tags.has("furnace80") ? "80% Furnace" : "Furnace");
  if (tags.has("heatpump")) out.push("Heat Pump");
  else if (tags.has("cooling")) out.push("Air Conditioner");
  if (tags.has("coil")) out.push("Coil");
  if (tags.has("airhandler")) out.push("Air Handler");
  if (tags.has("minisplit")) out.push("Mini-Split");
  if (tags.has("packaged")) out.push("Packaged Unit");
  if (tags.has("boiler")) out.push("Boiler");
  if (tags.has("waterheater")) out.push("Water Heater");
  if (tags.has("unitheater")) out.push("Unit Heater");
  const acc: Record<string, string> = {
    thermostat: "Thermostat", humidifier: "Humidifier", "air-cleaner": "Air Cleaner", "erv-hrv": "HRV / ERV",
    uv: "Germicidal Light", zoning: "Zoning", lineset: "Line Set", pad: "Pad", "chimney-liner": "Chimney Liner",
    "condensate-pump": "Condensate Pump",
  };
  for (const [k, label] of Object.entries(acc)) if (f.keys.has(k)) out.push(label);
  for (const label of Object.values(f.customLabels ?? {})) if (!out.includes(label)) out.push(label);
  return out;
}

/** The pricebook efficiency ("97% AFUE") for the items on a job. */
export async function fetchItemEfficiency(ids: string[]): Promise<Record<string, string | null>> {
  if (ids.length === 0) return {};
  const { data } = await supabase.from("item").select("id, efficiency").in("id", ids);
  return Object.fromEntries(((data ?? []) as { id: string; efficiency: string | null }[]).map((r) => [r.id, r.efficiency]));
}

/** The equipment categories added in Settings (not the built-in kinds). */
async function fetchCustomCats(): Promise<CustomCat[]> {
  const { data } = await supabase.from("equipment_category").select("key, label").eq("builtin", false);
  return (data ?? []) as CustomCat[];
}

/** Facts for a job's units, efficiency filled from the pricebook (no hooks — for loaders). */
export async function loadEquipFacts(units: EquipLike[]): Promise<EquipFacts> {
  const ids = [...new Set(units.map((u) => u.item_id).filter(Boolean) as string[])];
  const [eff, customs] = await Promise.all([fetchItemEfficiency(ids), fetchCustomCats()]);
  return equipFacts(units.map((u) => ({ ...u, efficiency: u.item_id ? eff[u.item_id] ?? null : null })), customs);
}

/** The job's equipment as facts, pricebook efficiency included. */
export function useJobEquipFacts(units: EquipLike[] | undefined): EquipFacts {
  const ids = [...new Set((units ?? []).map((u) => u.item_id).filter(Boolean) as string[])].sort();
  const { data: eff } = useQuery({
    queryKey: ["item_efficiency", ids.join(",")],
    enabled: ids.length > 0,
    staleTime: 10 * 60 * 1000,
    queryFn: () => fetchItemEfficiency(ids),
  });
  const { data: cats } = useEquipmentCategories();
  const customs = (cats ?? []).filter((c) => !c.builtin).map((c) => ({ key: c.key, label: c.label }));
  const list = (units ?? []).map((u) => ({ ...u, efficiency: u.item_id ? eff?.[u.item_id] ?? null : null }));
  return equipFacts(list, customs);
}

// ── INSTALL-SPEC-ADMIN: the bank lives in the database (Settings → Install Details) ──
interface SectionRow { key: string; title: string; sort_order: number; active: boolean }
export interface QuestionRow {
  key: string; section_key: string; label: string;
  options: string[]; amount_for: string[]; unit: string | null; note: string | null;
  hint: string | null; hint_when: string[]; is_text: boolean;
  when_tags: string[]; unless_tags: string[];
  when_answer: { key: string; in: string[] } | null;
  implied: { equip?: string; tag?: string; pick: string }[] | null;
  optional: boolean; locked: boolean; sort_order: number; active: boolean;
}

/** A database row → the engine's question. */
export function rowToQuestion(r: QuestionRow): SpecQuestion {
  const nz = <T,>(a: T[] | null | undefined) => (a && a.length ? a : undefined);
  return {
    key: r.key, section: r.section_key, label: r.label,
    options: r.is_text ? undefined : nz(r.options),
    amountFor: nz(r.amount_for), unit: r.unit ?? undefined, note: r.note ?? undefined,
    hint: r.hint ?? undefined, hintWhen: nz(r.hint_when), text: r.is_text || undefined,
    when: nz(r.when_tags), unless: nz(r.unless_tags),
    whenAnswer: r.when_answer ?? undefined, implied: nz(r.implied ?? []),
    optional: r.optional || undefined, locked: r.locked || undefined,
  };
}

/** The live bank (active sections + questions, in order); the built-in one if Settings has none. */
export async function loadInstallBank(): Promise<InstallBank> {
  const [{ data: secs, error: e1 }, { data: qs, error: e2 }] = await Promise.all([
    supabase.from("pegasus_install_section").select("key, title, sort_order, active").eq("active", true).order("sort_order").order("key"),
    supabase.from("pegasus_install_question").select("*").eq("active", true).order("sort_order").order("key"),
  ]);
  if (e1 || e2 || !secs?.length || !qs?.length) return BUILT_IN_BANK;
  return {
    sections: (secs as SectionRow[]).map((x) => ({ key: x.key, title: x.title })),
    questions: (qs as QuestionRow[]).map(rowToQuestion),
  };
}

/** The bank every reader uses — the office card, the work order, the warehouse, the summary, the phone. */
export function useInstallBank(): InstallBank {
  const { data } = useQuery({ queryKey: ["install_bank"], staleTime: 5 * 60 * 1000, queryFn: loadInstallBank });
  return data ?? BUILT_IN_BANK;
}
